import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { AdminPageHeader } from './AdminLayout';
import { Loader2, Plus, Trash2, Edit3, X } from 'lucide-react';

export function AdminLanguages() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get('/admin/languages').then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    if (editing?.id) await api.patch(`/admin/languages/${editing.id}`, data);
    else await api.post('/admin/languages', data);
    setShowForm(false); setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm('Delete this language?')) return; await api.delete(`/admin/languages/${id}`); load(); };

  return (
    <div>
      <AdminPageHeader title="Languages" description="Activate the languages available on your site. Default language is used when content is missing."
        actions={<button onClick={() => { setEditing({ code: '', name: '', native_name: '', flag: '', active: true, is_default: false }); setShowForm(true); }} className="btn-pill"><Plus size={16} /><span>Add language</span></button>} />
      <div className="rounded-3xl bg-white border border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
            <tr><th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Native</th><th className="text-left px-5 py-3">Active</th><th className="text-left px-5 py-3">Default</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} className="border-t border-black/5">
                <td className="px-5 py-3 font-mono uppercase font-semibold">{l.code}</td>
                <td className="px-5 py-3">{l.name}</td>
                <td className="px-5 py-3">{l.native_name}</td>
                <td className="px-5 py-3">{l.active ? <span className="text-green-700">Yes</span> : <span className="text-stone-500">No</span>}</td>
                <td className="px-5 py-3">{l.is_default && <span className="text-xs bg-[#1E2DFF] text-white px-2 py-0.5 rounded">default</span>}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => { setEditing(l); setShowForm(true); }} className="p-2 hover:text-[#1E2DFF]"><Edit3 size={14} /></button>
                  {!l.is_default && <button onClick={() => remove(l.id)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <Modal onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Edit language' : 'Add language'}>
        <LangForm initial={editing} onSubmit={save} />
      </Modal>}
    </div>
  );
}

function LangForm({ initial, onSubmit }) {
  const [f, setF] = useState(initial || { code: '', name: '', native_name: '', flag: '', active: true, is_default: false });
  const [busy, setBusy] = useState(false);
  const sub = async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(f); } finally { setBusy(false); } };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <form onSubmit={sub} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Code (en, es, pt...)" value={f.code} onChange={set('code')} required />
        <Input label="Flag (US, ES, BR)" value={f.flag} onChange={set('flag')} />
        <Input label="Name" value={f.name} onChange={set('name')} required />
        <Input label="Native name" value={f.native_name} onChange={set('native_name')} required />
      </div>
      <Check label="Active" checked={f.active} onChange={set('active')} />
      <Check label="Default language" checked={f.is_default} onChange={set('is_default')} />
      <button type="submit" disabled={busy} className="btn-pill">{busy ? <Loader2 className="animate-spin" size={16} /> : <span>Save</span>}</button>
    </form>
  );
}

export function AdminCountries() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get('/admin/countries').then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);
  const save = async (data) => {
    if (editing?.id) await api.patch(`/admin/countries/${editing.id}`, data);
    else await api.post('/admin/countries', data);
    setShowForm(false); setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm('Delete this country?')) return; await api.delete(`/admin/countries/${id}`); load(); };
  return (
    <div>
      <AdminPageHeader title="Countries" description="Activate the countries the platform serves. Used in the contact form and footer."
        actions={<button onClick={() => { setEditing({ code: '', name: '', flag: '', active: true }); setShowForm(true); }} className="btn-pill"><Plus size={16} /><span>Add country</span></button>} />
      <div className="rounded-3xl bg-white border border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500"><tr><th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Active</th><th></th></tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-black/5">
                <td className="px-5 py-3 font-mono uppercase font-semibold">{c.code}</td>
                <td className="px-5 py-3">{c.name}</td>
                <td className="px-5 py-3">{c.active ? <span className="text-green-700">Yes</span> : <span className="text-stone-500">No</span>}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 hover:text-[#1E2DFF]"><Edit3 size={14} /></button>
                  <button onClick={() => remove(c.id)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <Modal onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Edit country' : 'Add country'}>
        <CountryForm initial={editing} onSubmit={save} />
      </Modal>}
    </div>
  );
}

function CountryForm({ initial, onSubmit }) {
  const [f, setF] = useState(initial || { code: '', name: '', flag: '', active: true });
  const [busy, setBusy] = useState(false);
  const sub = async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(f); } finally { setBusy(false); } };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <form onSubmit={sub} className="space-y-3">
      <Input label="Code (US, MX...)" value={f.code} onChange={set('code')} required />
      <Input label="Name" value={f.name} onChange={set('name')} required />
      <Input label="Flag" value={f.flag} onChange={set('flag')} />
      <Check label="Active" checked={f.active} onChange={set('active')} />
      <button type="submit" disabled={busy} className="btn-pill">{busy ? <Loader2 className="animate-spin" size={16} /> : <span>Save</span>}</button>
    </form>
  );
}

export function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-display text-2xl">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Input({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-stone-700">{label}</span>
      <input {...props} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2.5 text-sm" />
    </label>
  );
}
export function Check({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={onChange} className="w-4 h-4 accent-[#0F0F10]" />
      <span className="text-sm text-stone-700">{label}</span>
    </label>
  );
}
